import PropTypes from 'prop-types';
import classNames from 'classnames';
import composeRefs from '@seznam/compose-react-refs';
import React, { useCallback, useRef } from 'react';
import { useMount } from 'react-use';
import { FiX, FiCalendar, FiClock } from 'react-icons/fi';

import { cls } from '@mssgme/helpers';
import { renderNode } from '../../../helpers';

import styles from './styles.scss';

Input.propTypes = {
    containerClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    className: PropTypes.string,
    hasError: PropTypes.bool,
    autoFocus: PropTypes.bool,
    refInput: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    beforeInput: PropTypes.any,
    afterInput: PropTypes.any,
    maxLength: PropTypes.number,
    type: PropTypes.oneOf(['text', 'email', 'search', 'number', 'password', 'tel', 'datetime-local', 'date', 'time', 'hidden']),
    size: PropTypes.oneOf(['small', 'normal']),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    inputStyle: PropTypes.oneOf(['default', 'grey']),
    isSelectAddon: PropTypes.bool,
    clearable: PropTypes.bool,
    hexInput: PropTypes.bool,
    hidden: PropTypes.bool,
    builderInput: PropTypes.bool,
};

Input.defaultProps = {
    containerClassName: '',
    hasError: false,
    disabled: false,
    type: 'text',
    inputStyle: 'default',
    builderInput: false,
    autoFocus: false,
    isSelectAddon: true,
};

const renderAddon = (node, className, isSelect) => {
    let addon = renderNode(node);

    if (addon) {
        if (typeof addon === 'string' || !isSelect) {
            addon = <div className={classNames(styles.contextBlock, className)}>{addon}</div>;
        } else if (isSelect) {
            addon = React.cloneElement(addon, cls(classNames(addon.props.className, styles.select), addon.props));
        }
    }

    return addon;
};

export default function Input({
    className,
    containerClassName,
    inputClassName,
    hasError,
    icon: Icon,
    size,
    beforeInput,
    afterInput,
    hexInput,
    hidden,
    maxLength,
    value,
    disabled,
    refInput,
    inputStyle,
    builderInput,
    autoFocus,
    isSelectAddon,
    clearable,
    type,
    ...rest
}) {
    const isFilled = clearable && !!value;
    const handleClear = useCallback(() => rest.onChange?.({ target: { name: rest.name, value: '' } }), [
        rest.name,
        rest.onChange,
    ]);

    const _beforeInput = renderAddon(beforeInput, className, isSelectAddon);
    const _afterInput = renderAddon(afterInput, className, isSelectAddon);

    const _rootClassName = classNames(
        styles.root,
        {
            [styles.icon]: Icon,
            [styles.before]: _beforeInput,
            [styles.after]: _afterInput,
            [styles.hidden]: hidden,
            [styles.hexInput]: hexInput,
            [styles.disabled]: disabled,
            [styles.builderInput]: builderInput,
            [styles.clearable]: isFilled,
        },
        inputStyle && styles[`root_${inputStyle}`],
        containerClassName
    );
    const _inputClassName = classNames(
        className,
        styles.input,
        {
            [styles.error]: hasError,
            [styles.paddingRight]: maxLength,
            [styles.small]: size === 'small',
            [styles.sizeNormal]: size === 'normal',
        },
        inputStyle && styles[inputStyle],
        inputClassName
    );
    const valueLength = value ? String(value).trim().length : 0;
    const ref = useRef();

    useMount(() => {
        if (autoFocus) {
            setTimeout(() => ref.current?.focus(), 100);
        }
    });

    return (
        <div className={_rootClassName}>
            {Icon && <Icon size={25} color="#C7C7CC" />}
            {_beforeInput}
            <div className={styles.inputWrapper} style={{ color: rest.style?.color }}>
                <input
                    ref={composeRefs(ref, refInput)}
                    className={_inputClassName}
                    value={value}
                    maxLength={maxLength}
                    disabled={disabled}
                    type={type}
                    {...rest}
                />
                {maxLength && <span className={styles.counter}>{maxLength - valueLength}</span>}
                {isFilled && (
                    <div
                        className={classNames(styles.clearAddon, !_afterInput && styles.hasRightAddon)}
                        onClick={handleClear}
                    >
                        <FiX size={14} style={{ marginBottom: -1 }} />
                    </div>
                )}
                { type === 'date' && <FiCalendar className={styles.customNativeIcon} /> }
                { type === 'time' && <FiClock className={styles.customNativeIcon} /> }
            </div>
            {_afterInput}
        </div>
    );
}
